/**
 * URL Shortener Utility
 * This utility provides functions to generate short URL codes and validate URLs.
 */

// Configuration for domains
const LINK_DOMAIN = "link.richs.my";
const REDIRECT_DOMAIN = "richs.com.my";

// Check if we're in development or production
const isDevelopment = () => {
  return !window.location.hostname.includes("richs.my");
};

// Get the appropriate domains based on environment
const getDomains = () => {
  if (isDevelopment()) {
    // For development (e.g., CodeSandbox)
    return {
      appDomain: window.location.origin,
      linkDomain: window.location.origin,
      redirectDomain: "https://richs.com.my",
    };
  }

  return {
    linkDomain: `https://${LINK_DOMAIN}`,
    redirectDomain: `https://${REDIRECT_DOMAIN}`,
  };
};

// Characters used for generating short URLs (alphanumeric without ambiguous characters)
const CHARACTERS = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const CODE_LENGTH = 6; // Length of the generated short codes

/**
 * Generates a random short code for URLs
 * Uses cryptographically secure random values when available
 * @param {number} length - Length of the code to generate (default: 6)
 * @returns {string} - Generated short code
 */
export const generateShortCode = (length = CODE_LENGTH) => {
  let result = "";
  const charactersLength = CHARACTERS.length;

  // Use crypto.getRandomValues when available for better randomness
  if (window.crypto && window.crypto.getRandomValues) {
    const randomValues = new Uint32Array(length);
    window.crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
      result += CHARACTERS.charAt(randomValues[i] % charactersLength);
    }
  } else {
    // Fallback to Math.random if crypto is not available
    for (let i = 0; i < length; i++) {
      result += CHARACTERS.charAt(Math.floor(Math.random() * charactersLength));
    }
  }

  return result;
};

/**
 * Validates a URL to ensure it's properly formatted and safe
 * Enhanced to prevent malicious URLs
 * @param {string} url - URL to validate
 * @returns {boolean} - Whether the URL is valid and safe
 */
export const isValidUrl = (url) => {
  try {
    // Try to create a URL object - this will throw for invalid URLs
    const urlObj = new URL(url);

    // Block javascript: protocol (can be used for XSS)
    if (urlObj.protocol === "javascript:") {
      console.warn("Blocked potentially malicious javascript: URL");
      return false;
    }

    // Block data: protocol (can be used for data exfiltration)
    if (urlObj.protocol === "data:") {
      console.warn("Blocked potentially malicious data: URL");
      return false;
    }

    // Block file: protocol (can expose local files)
    if (urlObj.protocol === "file:") {
      console.warn("Blocked potentially malicious file: URL");
      return false;
    }

    // Allow only http and https protocols
    if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
      console.warn(`Blocked URL with disallowed protocol: ${urlObj.protocol}`);
      return false;
    }

    // Check for potentially dangerous hostnames
    const blockedHosts = ["localhost", "127.0.0.1", "0.0.0.0", "[::1]"];

    if (blockedHosts.includes(urlObj.hostname)) {
      console.warn(
        `Blocked URL with potentially dangerous hostname: ${urlObj.hostname}`
      );
      return false;
    }

    // Ensure URL has a valid hostname
    if (!urlObj.hostname) {
      console.warn("URL has no hostname");
      return false;
    }

    return true;
  } catch (error) {
    console.warn("URL validation error:", error.message);
    return false;
  }
};

/**
 * Creates a full short URL with the link.richs.my domain
 * @param {string} code - The short code
 * @returns {string} - Complete short URL
 */
export const createFullShortUrl = (code) => {
  const { linkDomain } = getDomains();
  // Ensure the domain doesn't have a trailing slash
  const cleanDomain = linkDomain.endsWith("/")
    ? linkDomain.slice(0, -1)
    : linkDomain;
  return `${cleanDomain}/${code}`;
};

/**
 * Extracts the original domain from a URL for display purposes
 * @param {string} url - The full URL
 * @returns {string} - Domain name (e.g., "example.com")
 */
export const extractDomain = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch (error) {
    return url;
  }
};

/**
 * Normalizes a URL by ensuring it has a protocol
 * @param {string} url - URL to normalize
 * @returns {string} - Normalized URL with protocol
 */
export const normalizeUrl = (url) => {
  // If URL doesn't start with http:// or https://, add https://
  if (!url.match(/^https?:\/\//i)) {
    return `https://${url}`;
  }
  return url;
};

/**
 * Generates a unique short code ensuring it doesn't exist in the database
 * @param {Function} checkExists - Async function to check if a code exists
 * @param {number} length - Length of the code to generate
 * @returns {Promise<string>} - Generated unique short code
 */
export const generateUniqueCode = async (checkExists, length = CODE_LENGTH) => {
  // Maximum attempts to find a unique code
  const MAX_ATTEMPTS = 10;
  let attempts = 0;

  while (attempts < MAX_ATTEMPTS) {
    const code = generateShortCode(length);
    // Check if the code already exists
    const exists = await checkExists(code);

    if (!exists) {
      return code;
    }

    attempts++;
  }

  // If we've made too many attempts, increase the code length and try again
  return generateUniqueCode(checkExists, length + 1);
};

export default {
  generateShortCode,
  isValidUrl,
  createFullShortUrl,
  extractDomain,
  normalizeUrl,
  generateUniqueCode,
  isDevelopment,
  getDomains,
};
